import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableUserSelect } from '../components/SearchableUserSelect';
import { HandCoins, Loader2, Download, FileSpreadsheet, FileText, Users, IndianRupee, CheckCircle2, RotateCcw } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const formatINR = (amount) => {
  const n = Number(amount || 0);
  if (Number.isNaN(n)) return '₹0';
  const sign = n < 0 ? '-' : '';
  const fixed = Math.abs(n).toFixed(0);
  let lastThree = fixed.slice(-3);
  const otherNumbers = fixed.slice(0, -3);
  if (otherNumbers !== '') lastThree = ',' + lastThree;
  const formatted = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  return `₹${sign}${formatted}`;
};

const EMPTY_FILTERS = { date_from: '', date_to: '', referrer_id: '', category_id: '', status_id: '', min_amount: '', payout_status: '' };

const ReferralCommissionReport = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [referrers, setReferrers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [payoutBusyId, setPayoutBusyId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [refRes, catRes, statusRes] = await Promise.all([
          api.get('/users/referrers').catch(() => ({ data: [] })),
          api.get('/master/primary-categories').catch(() => ({ data: [] })),
          api.get('/master/lead-status').catch(() => ({ data: [] })),
        ]);
        setReferrers(refRes.data || []);
        setCategories(catRes.data || []);
        setStatuses(statusRes.data || []);
      } catch (e) {
        toast.error('Failed to load filter options');
      }
    })();
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.referrer_id) params.referrer_id = filters.referrer_id;
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.status_id) params.status_id = filters.status_id;
    if (filters.min_amount) params.min_amount = parseFloat(filters.min_amount);
    if (filters.payout_status) params.payout_status = filters.payout_status;
    return params;
  }, [filters]);

  const fetchReport = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/reports/referral-commissions', { params: buildParams() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load Referral Commission report');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get('/reports/referral-commissions', {
        params: { ...buildParams(), export: format },
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `referral-commission-report.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  const rows = data?.rows || [];
  const summary = data?.summary || [];

  const markPaid = async (row) => {
    const note = window.prompt(`Mark ${row.referral_commission_amount ? formatINR(row.referral_commission_amount) : ''} referral commission as PAID to ${row.referrer_name}? Optional note (payment ref, mode, etc.):`, '');
    if (note === null) return; // cancelled
    setPayoutBusyId(row.lead_id);
    try {
      await api.post(`/leads/${row.lead_id}/referral-payout/mark-paid`, { note: note || null });
      toast.success(`Marked as paid to ${row.referrer_name}`);
      fetchReport(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark as paid');
    } finally {
      setPayoutBusyId('');
    }
  };

  const markUnpaid = async (row) => {
    if (!window.confirm(`Revert this referral commission back to Pending?`)) return;
    setPayoutBusyId(row.lead_id);
    try {
      await api.post(`/leads/${row.lead_id}/referral-payout/mark-unpaid`);
      toast.success('Reverted to Pending');
      fetchReport(true);
    } catch (e) {
      toast.error('Could not revert');
    } finally {
      setPayoutBusyId('');
    }
  };

  return (
    <div className="space-y-6 p-1" data-testid="referral-commission-report">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-100 to-blue-100 dark:from-indigo-950/40 dark:to-blue-950/40">
          <HandCoins className="w-6 h-6 text-indigo-700 dark:text-indigo-300" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Referral Commission Report</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Commission owed to each Sales Associate / Selling Partner referrer, by period</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="referral-report-date-from" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="referral-report-date-to" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Referrer</Label>
              <SearchableUserSelect
                value={filters.referrer_id}
                onChange={(v) => setFilters(f => ({ ...f, referrer_id: v }))}
                users={referrers}
                placeholder="All referrers"
                testId="referral-report-referrer-select"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Category</Label>
              <Select value={filters.category_id || '__all__'} onValueChange={(v) => setFilters(f => ({ ...f, category_id: v === '__all__' ? '' : v }))}>
                <SelectTrigger data-testid="referral-report-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Status</Label>
              <Select value={filters.status_id || '__all__'} onValueChange={(v) => setFilters(f => ({ ...f, status_id: v === '__all__' ? '' : v }))}>
                <SelectTrigger data-testid="referral-report-status-select"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All statuses</SelectItem>
                  {statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min. Commission (₹)</Label>
              <Input type="number" min="0" placeholder="0" value={filters.min_amount} onChange={(e) => setFilters(f => ({ ...f, min_amount: e.target.value }))} data-testid="referral-report-min-amount" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Payout Status</Label>
              <Select value={filters.payout_status || '__all__'} onValueChange={(v) => setFilters(f => ({ ...f, payout_status: v === '__all__' ? '' : v }))}>
                <SelectTrigger data-testid="referral-report-payout-status-select"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            {Object.values(filters).some(Boolean) ? (
              <Button variant="outline" size="sm" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="referral-report-clear-filters">Clear filters</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={exporting === 'csv' || rows.length === 0} onClick={() => handleExport('csv')} data-testid="referral-report-export-csv">
                {exporting === 'csv' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} CSV
              </Button>
              <Button variant="outline" size="sm" disabled={exporting === 'xlsx' || rows.length === 0} onClick={() => handleExport('xlsx')} data-testid="referral-report-export-xlsx">
                {exporting === 'xlsx' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />} Excel
              </Button>
              <Button variant="outline" size="sm" disabled={exporting === 'pdf' || rows.length === 0} onClick={() => handleExport('pdf')} data-testid="referral-report-export-pdf">
                {exporting === 'pdf' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />} PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></CardContent></Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HandCoins className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No referral commission found for these filters.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 border-indigo-200 dark:border-indigo-900">
              <CardContent className="py-5">
                <p className="text-xs font-medium uppercase tracking-wider text-indigo-700 dark:text-indigo-300">Total Referral Commission</p>
                <p className="text-3xl font-bold text-indigo-900 dark:text-indigo-100 mt-1" data-testid="referral-report-total-commission">{formatINR(data.total_referral_commission)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-900">
              <CardContent className="py-5">
                <p className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Paid Out</p>
                <p className="text-3xl font-bold text-emerald-900 dark:text-emerald-100 mt-1" data-testid="referral-report-total-paid">{formatINR(data.total_paid)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-900">
              <CardContent className="py-5">
                <p className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">Pending Payout</p>
                <p className="text-3xl font-bold text-amber-900 dark:text-amber-100 mt-1" data-testid="referral-report-total-pending">{formatINR(data.total_pending)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/30 dark:to-gray-950/30 border-slate-200 dark:border-slate-800">
              <CardContent className="py-5">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">Referrers</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mt-1" data-testid="referral-report-total-referrers">{summary.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Per-referrer summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> By Referrer</CardTitle>
              <CardDescription>Total referral commission per Sales Associate / Selling Partner (paid vs pending)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {summary.map((s) => (
                  <div key={s.referrer_id} className="flex items-center justify-between gap-3 p-2.5 rounded-md border hover:bg-muted/40" data-testid={`referral-summary-row-${s.referrer_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.referrer_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {s.referrer_company_name || '—'} · {s.lead_count} lead{s.lead_count !== 1 ? 's' : ''} · Deals {formatINR(s.total_deal_value)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-indigo-700 dark:text-indigo-300 flex items-center justify-end gap-1">
                        <IndianRupee className="w-3.5 h-3.5" />{formatINR(s.total_referral_commission).replace('₹', '')}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Paid {formatINR(s.paid_amount)} · Pending {formatINR(s.pending_amount)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Detail rows */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Lead-level Detail</CardTitle>
              <CardDescription>{rows.length} lead(s) with a referral commission</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Lead</th>
                      <th className="py-2 pr-3">Referrer</th>
                      <th className="py-2 pr-3">Category</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 text-right">Closed Deal Value</th>
                      <th className="py-2 pr-3 text-right">Referral %</th>
                      <th className="py-2 pr-3 text-right">Commission</th>
                      <th className="py-2 pr-3">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.lead_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`referral-report-row-${r.lead_id}`}>
                        <td className="py-2 pr-3 text-muted-foreground">{r.period_date || '—'}</td>
                        <td className="py-2 pr-3 font-medium">{r.lead_title}</td>
                        <td className="py-2 pr-3">
                          {r.referrer_name}
                          {r.referrer_company_name && <span className="text-muted-foreground"> — {r.referrer_company_name}</span>}
                        </td>
                        <td className="py-2 pr-3"><Badge variant="outline" className="text-[10px]">{r.category_name || '—'}</Badge></td>
                        <td className="py-2 pr-3 text-muted-foreground">{r.status_name || '—'}</td>
                        <td className="py-2 pr-3 text-right">{formatINR(r.closed_deal_value)}</td>
                        <td className="py-2 pr-3 text-right">{r.referral_commission_level_name ? `${r.referral_commission_level_name} (${r.referral_commission_percent}%)` : `${r.referral_commission_percent}%`}</td>
                        <td className="py-2 pr-3 text-right font-bold text-indigo-700 dark:text-indigo-300">{formatINR(r.referral_commission_amount)}</td>
                        <td className="py-2 pr-3">
                          {r.referral_payout_status === 'paid' ? (
                            <div className="flex items-center gap-1.5">
                              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300 gap-1" data-testid={`payout-paid-badge-${r.lead_id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Paid
                              </Badge>
                              <Button
                                size="icon" variant="ghost" className="h-6 w-6"
                                disabled={payoutBusyId === r.lead_id}
                                onClick={() => markUnpaid(r)}
                                data-testid={`mark-referral-unpaid-${r.lead_id}`}
                                title="Revert to pending"
                              >
                                {payoutBusyId === r.lead_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm" variant="outline"
                              disabled={payoutBusyId === r.lead_id}
                              onClick={() => markPaid(r)}
                              data-testid={`mark-referral-paid-${r.lead_id}`}
                            >
                              {payoutBusyId === r.lead_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                              Mark Paid
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReferralCommissionReport;
